import { useLingui as _useLingui } from "../lib/i18n/useLingui";
import { Tooltip } from "../components/Tooltip";
import { ChevronLeft, Trash01, Upload01, XClose } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
import { Button } from "./Button";
const barVariants = cva("flex flex-row items-center gap-3 px-4", {
  variants: {
    position: {
      top: "border-b",
      bottom: "border-t"
    },
    variant: {
      full: "fixed left-0 right-0 z-30 backdrop-blur border-border-dark/70 bg-background/80",
      default: "relative z-10 border-border bg-background"
    },
    size: {
      sm: "h-12",
      md: "h-14"
    }
  },
  compoundVariants: [{
    position: "top",
    variant: "full",
    class: "top-0"
  }, {
    position: "bottom",
    variant: "full",
    class: "bottom-0"
  }],
  defaultVariants: {
    position: "top",
    variant: "full",
    size: "md"
  }
});
/**
 * A header or footer action bar that anchors a page, panel, or modal with a title,
 * optional description, and left/center/right action slots. Set `position` to `top`
 * or `bottom`, and `variant` to `full` (spans the viewport) or `default` (scoped to
 * its parent container). Use Bar.ButtonBar for ready-made action layouts; for a
 * floating, transient action surface over content, use HoveringBar instead.
 * @summary Page or panel action bar.
 */
export function Bar({
  title,
  description,
  tooltip,
  leftActions,
  centerActions,
  rightActions,
  className,
  position,
  variant,
  size
}) {
  const titleClasses = cn("text-foreground", "heading-base truncate");
  return /*#__PURE__*/React.createElement("div", {
    className: cn(barVariants({
      position,
      variant,
      size
    }), className)
  }, leftActions && /*#__PURE__*/React.createElement("div", {
    className: "flex gap-1"
  }, leftActions), title && /*#__PURE__*/React.createElement("div", {
    className: titleClasses
  }, tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    tooltipTriggerAsChild: true,
    trigger: /*#__PURE__*/React.createElement(React.Fragment, null, typeof title === "string" ? /*#__PURE__*/React.createElement("span", null, title) : title, description),
    label: tooltip
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, typeof title === "string" ? /*#__PURE__*/React.createElement("span", null, title) : title, description)), centerActions && /*#__PURE__*/React.createElement("div", {
    className: "flex gap-1"
  }, centerActions), /*#__PURE__*/React.createElement("div", {
    className: "flex-grow"
  }), rightActions && /*#__PURE__*/React.createElement("div", {
    className: "flex gap-1"
  }, rightActions));
}
/**
 * Ready-made action layouts for a Bar: `close`, `back`, `validate`
 * (cancel/save pair), or `conversation` (delete/share).
 * @summary Preset Bar action layouts.
 */
Bar.ButtonBar = function (props) {
  const {
    _: _t
  } = _useLingui();
  switch (props.variant) {
    case "back":
      return /*#__PURE__*/React.createElement(Button, {
        size: "sm",
        icon: ChevronLeft,
        variant: "ghost",
        tooltip: _t(
        /** i18n */
        {
          id: "iH8pgl",
          message: "Back"
        }),
        onClick: props.onBack
      });
    case "close":
      return /*#__PURE__*/React.createElement(Button, {
        size: "sm",
        icon: XClose,
        variant: "ghost",
        tooltip: _t(
        /** i18n */
        {
          id: "yz7wBu",
          message: "Close"
        }),
        onClick: props.onClose
      });
    case "validate":
      return /*#__PURE__*/React.createElement(React.Fragment, null, props.cancelButtonProps && /*#__PURE__*/React.createElement(Button, {
        ...props.cancelButtonProps
      }), props.saveButtonProps && /*#__PURE__*/React.createElement(Button, {
        ...props.saveButtonProps
      }));
    case "conversation":
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
        size: "sm",
        icon: Trash01,
        tooltip: _t(
        /** i18n */
        {
          id: "cnGeoo",
          message: "Delete"
        }),
        variant: "ghost",
        onClick: props.onDelete
      }), /*#__PURE__*/React.createElement(Button, {
        size: "sm",
        label: _t(
        /** i18n */
        {
          id: "Z8lGw6",
          message: "Share"
        }),
        icon: Upload01,
        variant: "ghost",
        onClick: props.onShare
      }));
    default:
      return null;
  }
};

// BarHeader component - convenience wrapper for top-positioned Bar

/**
 * Convenience wrapper for a top-positioned Bar with a required title. Use it to
 * frame a page or panel with a persistent header.
 * @summary Top-positioned Bar.
 */
export function BarHeader({
  title,
  description,
  tooltip,
  leftActions,
  centerActions,
  rightActions,
  className,
  variant,
  size
}) {
  return /*#__PURE__*/React.createElement(Bar, {
    position: "top",
    title: title,
    description: description,
    tooltip: tooltip,
    leftActions: leftActions,
    centerActions: centerActions,
    rightActions: rightActions,
    className: className,
    variant: variant,
    size: size
  });
}
BarHeader.ButtonBar = Bar.ButtonBar;

// BarFooter component - convenience wrapper for bottom-positioned Bar

/**
 * Convenience wrapper for a bottom-positioned Bar, e.g. to pin save/cancel
 * controls under a scrolling form.
 * @summary Bottom-positioned Bar.
 */
export function BarFooter({
  leftActions,
  rightActions,
  className,
  variant,
  size
}) {
  return /*#__PURE__*/React.createElement(Bar, {
    position: "bottom",
    leftActions: leftActions,
    rightActions: rightActions,
    className: className,
    variant: variant,
    size: size
  });
}
BarFooter.ButtonBar = Bar.ButtonBar;
//# sourceMappingURL=Bar.js.map